import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { FigmaRestApi } from "@zeytal/types";

import { roundNumber } from "@endgame/utils/roundNumber";

const paintToHex = (color: FigmaRestApi.RGBA) => {
  const { r, g, b, a } = color;

  const red = parseFloat((r * 255).toFixed(0))
    .toString(16)
    .padStart(2, "0");
  const green = parseFloat((g * 255).toFixed(0))
    .toString(16)
    .padStart(2, "0");
  const blue = parseFloat((b * 255).toFixed(0))
    .toString(16)
    .padStart(2, "0");
  const alpha = parseFloat((a * 255).toFixed(0))
    .toString(16)
    .padStart(2, "0");

  return `#${red}${green}${blue}${a !== 1 ? alpha : ""}`;
};

const paintToRgba = (color: FigmaRestApi.RGBA) => {
  const { r, g, b, a } = color;

  const red = (r * 255).toFixed(0);
  const green = (g * 255).toFixed(0);
  const blue = (b * 255).toFixed(0);
  const alpha = roundNumber(a, 2);

  return `rgb(${red}, ${green}, ${blue}, ${alpha})`;
};

const paintToHsla = (color: FigmaRestApi.RGBA) => {
  const { r, g, b, a } = color;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = (max + min) / 2;
  let saturation = (max + min) / 2;
  let lightness = (max + min) / 2;
  const alpha = roundNumber(a, 2);

  if (max === min) {
    hue = saturation = 0; // achromatic
  } else {
    const d = max - min;
    saturation = lightness > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      case b:
        hue = (r - g) / d + 4;
        break;
    }
    hue /= 6;
  }

  hue = Math.round(hue * 360);
  saturation = Math.round(saturation * 100);
  lightness = Math.round(lightness * 100);

  return `hsl(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
};

export const paintToSelectedFormat = (
  colorsConfig: ZeytalCLIPureConfig["styles"]["formats"]["colors"],
  color: FigmaRestApi.RGBA,
) => {
  const { format } = colorsConfig;

  if (format === "hex") {
    return paintToHex(color);
  } else if (format === "rgba") {
    return paintToRgba(color);
  } else if (format === "hsla") {
    return paintToHsla(color);
  }

  return paintToHex(color);
};

export const calculateGradientColorStops = (
  stylesFormatsConfig: ZeytalCLIPureConfig["styles"]["formats"],
  gradientStops: FigmaRestApi.ColorStop[],
) => {
  let value = "";

  gradientStops.forEach((stop, index) => {
    const color = paintToSelectedFormat(stylesFormatsConfig.colors, stop.color);
    const position = (stop.position ?? 0) * 100;

    if (index > 0) {
      value += " ";
    }
    value += `${color} ${position}%`;
    if (index < gradientStops.length - 1) {
      value += ",";
    }
  });

  return value;
};

export const calculateLinearGradientDegree = (
  gradientHandlePositions: FigmaRestApi.Vector[],
) => {
  if (gradientHandlePositions.length < 2) {
    throw new Error("gradientHandlePositions must contain at least two points");
  }

  const [handle1, handle2] = gradientHandlePositions as [
    FigmaRestApi.Vector,
    FigmaRestApi.Vector,
  ];

  const angle = roundNumber(
    ((Math.atan2(handle2.y - handle1.y, handle2.x - handle1.x) * 180) /
      Math.PI +
      450) %
      360,
    2,
  );

  return `${angle}deg, `;
};

export const calculateRadialGradientPosition = (
  gradientHandlePositions: FigmaRestApi.Vector[],
) => {
  if (gradientHandlePositions.length < 3) {
    throw new Error("gradientHandlePositions must contain at least two points");
  }

  const [handle1, handle2, handle3] = gradientHandlePositions as [
    FigmaRestApi.Vector,
    FigmaRestApi.Vector,
    FigmaRestApi.Vector,
  ];

  // Use handle1 point for the gradient position
  const positionX = roundNumber(handle1.x * 100, 2);
  const positionY = roundNumber(handle1.y * 100, 2);

  // Calculate the distances for the gradient sizes
  const deltaX1 = handle2.x - handle1.x;
  const deltaY1 = handle2.y - handle1.y;
  const distance1 = Math.sqrt(deltaX1 * deltaX1 + deltaY1 * deltaY1);

  const deltaX2 = handle3.x - handle1.x;
  const deltaY2 = handle3.y - handle1.y;
  const distance2 = Math.sqrt(deltaX2 * deltaX2 + deltaY2 * deltaY2);

  const sizeX = roundNumber(distance1 * 100, 2);
  const sizeY = roundNumber(distance2 * 100, 2);

  // Create the gradient position and shape string
  return `${sizeX}% ${sizeY}% at ${positionX}% ${positionY}%, `;
};

export const calculateAngularGradientPosition = (
  gradientHandlePositions: FigmaRestApi.Vector[],
) => {
  if (gradientHandlePositions.length < 2) {
    throw new Error("gradientHandlePositions must contain at least two points");
  }

  const [handle1, handle2] = gradientHandlePositions as [
    FigmaRestApi.Vector,
    FigmaRestApi.Vector,
  ];

  // Calculate the angle for the gradient from handle1 to handle2
  const angle = roundNumber(
    ((Math.atan2(handle2.y - handle1.y, handle2.x - handle1.x) * 180) /
      Math.PI +
      450) %
      360,
    2,
  );

  // Use handle1 point for the gradient position
  const positionX = roundNumber(handle1.x * 100, 2);
  const positionY = roundNumber(handle1.y * 100, 2);

  // Create the gradient angle and position string
  return `from ${angle}deg at ${positionX}% ${positionY}%, `;
};
